import type { ComponentProps } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion";
import { LINE_BREAKS } from "~/components/ui/max-width-line-break";
import { SectionTitle } from "~/components/ui/section-title";
import { useObdCompatibilityChecker } from "~/components/products/pdp/ObdCompatibilityCheckerContext";
import type { ProductFaqContent } from "~/data/products/types";
import { APP_STORE_URL, GOOGLE_PLAY_URL, WEB_APP_ACTIVATE_URL } from "~/lib/external-links";

interface FaqSectionProps {
  content: ProductFaqContent;
  /** Open the first question on load (the fleet PDP); the consumer PDPs start collapsed. */
  defaultOpenFirst?: boolean;
}

const LINK_CLASS = "underline underline-offset-2";

function ExternalLink({ children, ...props }: ComponentProps<"a">) {
  return (
    <a target="_blank" rel="noreferrer" className={LINK_CLASS} {...props}>
      {children}
    </a>
  );
}

// A link, not a <button>, because buttons can't break across lines. The href falls back
// to the compatibility section.
function CheckerLink({ onOpen, children, ...props }: ComponentProps<"a"> & { onOpen: () => void }) {
  return (
    <a
      href="#compatibility"
      onClick={(event) => {
        event.preventDefault();
        onOpen();
      }}
      className={LINK_CLASS}
      {...props}
    >
      {children}
    </a>
  );
}

// Inside the fleet PDP's .fleet scope the `fleet:` classes give the dark version (Figma
// 10480:408146), matching FleetFaq on /fleet; the consumer PDPs are unaffected.
export function FaqSection({ content, defaultOpenFirst = false }: FaqSectionProps) {
  const { t } = useTranslation("products");
  const { setOpen } = useObdCompatibilityChecker();

  // Tags answers may use to link out: <checker> opens the compatibility drawer.
  const answerLinks = {
    ...LINE_BREAKS,
    checker: <CheckerLink data-sid="pdp-faq-link:checker" onOpen={() => setOpen(true)} />,
    ios: <ExternalLink data-sid="pdp-faq-link:ios" href={APP_STORE_URL} />,
    android: <ExternalLink data-sid="pdp-faq-link:android" href={GOOGLE_PLAY_URL} />,
    web: <ExternalLink data-sid="pdp-faq-link:web" href={WEB_APP_ACTIVATE_URL} />,
  };

  return (
    <section id="faq" className="bg-figma-background-surface-bg-secondary fleet:bg-background scroll-mt-20 py-8 lg:py-15">
      <SectionTitle className="wrap text-text-heading lg:fleet:text-left mb-6 text-center lg:mb-10">
        {t(content.headline)}
      </SectionTitle>
      <Accordion type="single" collapsible defaultValue={defaultOpenFirst ? "item-0" : undefined} className="wrap flex flex-col gap-1">
        {content.items.map((item, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="fleet:border-bg-alt bg-transparent">
            <AccordionTrigger data-sid={`pdp-faq-trigger:${i}`} className="fleet:text-text-heading-alt fleet:gap-0 gap-4 text-left">
              <span>
                <Trans t={t} i18nKey={item.question} components={LINE_BREAKS} />
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Trans t={t} i18nKey={item.answer} components={answerLinks} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
